import { useCallback } from 'react';
import MonacoEditor from '@monaco-editor/react';
import { useAppStore } from '../store';
import { X, Circle } from 'lucide-react';

export function Editor() {
    const { files, activeFilePath, setActiveFile, updateFile } = useAppStore();

    const activeFile = activeFilePath ? files.get(activeFilePath) : null;

    // Get open files (files that have been viewed)
    const openFiles = Array.from(files.values()).filter(f => f.isDirty || f.path === activeFilePath);

    const handleEditorChange = useCallback((value: string | undefined) => {
        if (activeFilePath && value !== undefined) {
            updateFile(activeFilePath, value);
        }
    }, [activeFilePath, updateFile]);

    const getLanguage = (path: string): string => {
        const ext = path.split('.').pop()?.toLowerCase();
        const languages: Record<string, string> = {
            tsx: 'typescript',
            ts: 'typescript',
            jsx: 'javascript',
            js: 'javascript',
            css: 'css',
            json: 'json',
            html: 'html',
            md: 'markdown',
        };
        return languages[ext || ''] || 'plaintext';
    };

    if (!activeFile) {
        return (
            <div className="flex-1 flex items-center justify-center bg-background text-text-muted">
                <div className="text-center">
                    <p className="text-lg">No file selected</p>
                    <p className="text-sm mt-1">Select a file from the sidebar or generate code</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-background">
            {/* Tab bar */}
            <div className="h-10 flex items-center border-b border-border bg-background-secondary overflow-x-auto">
                {openFiles.map(file => (
                    <button
                        key={file.path}
                        onClick={() => setActiveFile(file.path)}
                        className={`flex items-center gap-2 h-full px-3 border-r border-border
                        text-sm transition-colors group
                        ${file.path === activeFilePath
                                ? 'bg-background text-text-primary'
                                : 'text-text-secondary hover:bg-surface'}`}
                    >
                        {file.isDirty && (
                            <Circle className="w-2 h-2 fill-accent-primary text-accent-primary flex-shrink-0" />
                        )}
                        <span className="truncate max-w-[120px]">{file.path.split('/').pop()}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                // Close tab logic would go here
                            }}
                            className="opacity-0 group-hover:opacity-100 hover:bg-surface-hover rounded p-0.5 transition-opacity"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </button>
                ))}
            </div>

            {/* Editor */}
            <div className="flex-1">
                <MonacoEditor
                    height="100%"
                    language={getLanguage(activeFile.path)}
                    value={activeFile.content}
                    onChange={handleEditorChange}
                    theme="vs-dark"
                    options={{
                        fontSize: 14,
                        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                        fontLigatures: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        lineNumbers: 'on',
                        renderLineHighlight: 'line',
                        cursorBlinking: 'smooth',
                        cursorSmoothCaretAnimation: 'on',
                        smoothScrolling: true,
                        padding: { top: 16, bottom: 16 },
                        automaticLayout: true,
                        tabSize: 2,
                        insertSpaces: true,
                        bracketPairColorization: { enabled: true },
                        guides: {
                            indentation: true,
                            bracketPairs: true,
                        },
                    }}
                    beforeMount={(monaco) => {
                        // Define custom theme
                        monaco.editor.defineTheme('vibeflare-dark', {
                            base: 'vs-dark',
                            inherit: true,
                            rules: [
                                { token: 'comment', foreground: '6b7280', fontStyle: 'italic' },
                                { token: 'keyword', foreground: 'f38020' }, // Cloudflare Orange
                                { token: 'string', foreground: '10b981' },
                                { token: 'number', foreground: 'faad3f' },
                                { token: 'type', foreground: '056dff' },
                            ],
                            colors: {
                                'editor.background': '#0f1114',
                                'editor.foreground': '#f5f7fa',
                                'editor.lineHighlightBackground': '#1b1e23',
                                'editor.selectionBackground': '#f3802033',
                                'editorCursor.foreground': '#f38020',
                                'editorLineNumber.foreground': '#4b5563',
                                'editorLineNumber.activeForeground': '#f38020',
                            },
                        });
                    }}
                    onMount={(_editor, monaco) => {
                        monaco.editor.setTheme('vibeflare-dark');

                        // Configure TypeScript compiler options
                        monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
                            target: monaco.languages.typescript.ScriptTarget.ES2020,
                            allowNonTsExtensions: true,
                            moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
                            module: monaco.languages.typescript.ModuleKind.CommonJS,
                            noEmit: true,
                            esModuleInterop: true,
                            jsx: monaco.languages.typescript.JsxEmit.React,
                            reactNamespace: 'React',
                            allowJs: true,
                            typeRoots: ['node_modules/@types'],
                        });

                        // Add React types to prevent "Cannot find module 'react'" errors
                        const reactTypes = `
                            declare module 'react' {
                                export = React;
                                export as namespace React;
                                namespace React {
                                    function useState<T>(initialState: T | (() => T)): [T, (newState: T | ((prevState: T) => T)) => void];
                                    function useEffect(effect: () => void | (() => void), deps?: ReadonlyArray<any>): void;
                                    function useCallback<T extends (...args: any[]) => any>(callback: T, deps: ReadonlyArray<any>): T;
                                    function useRef<T>(initialValue: T): { current: T };
                                    function createElement(type: any, props?: any, ...children: any[]): any;
                                    // Add minimal types needed for the demo
                                    interface ReactElement<P = any, T extends string | JSXElementConstructor<any> = string | JSXElementConstructor<any>> {
                                        type: T;
                                        props: P;
                                        key: Key | null;
                                    }
                                }
                            }
                            declare module 'lucide-react' {
                                export const Plus: any;
                                export const Minus: any;
                                export const X: any;
                                export const Check: any;
                                export const User: any;
                                export const Settings: any;
                                export const Search: any;
                                export const Menu: any;
                                export const Home: any;
                                export const Loader2: any;
                                export const Send: any;
                                export const Sparkles: any;
                                export const ChevronDown: any;
                                export const ChevronRight: any;
                                export const ChevronLeft: any;
                                export const LogOut: any;
                                export const Trash2: any;
                            }
                        `;
                        monaco.languages.typescript.typescriptDefaults.addExtraLib(reactTypes, 'file:///node_modules/@types/react/index.d.ts');
                    }}
                />
            </div>
        </div>
    );
}
